import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import { Col, Form, Row } from 'react-bootstrap';
import CreatableSelect from 'react-select/creatable';
import { useParams } from 'react-router-dom';

import { CLIENT_GLOBAL_VENDOR } from 'lib/networking/endpoints';
import { CUT_DRY_CLIENT_ID } from 'lib/constants';
import ImageCategoryModal from 'components/manage_vendors/vendor_details/ImageCategoryModal';
import useGet from 'lib/hooks/useGet';

function ImageCategorySetting({ imageCategory }) {
  const { vendorID } = useParams();
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [isNewCategory, setIsNewCategory] = useState(false);

  // Fetch existing client_global_vendor configs to get image category keys
  const { data: { data: clientGlobalVendor } = {}, loading } = useGet(
    CLIENT_GLOBAL_VENDOR,
    {
      client_id: CUT_DRY_CLIENT_ID,
      global_vendor_id: vendorID,
    },
    !vendorID,
  );

  // Update category options when data is fetched
  useEffect(() => {
    if (clientGlobalVendor?.configs?.image_category_setting) {
      const imageCategoryKeys = Object.keys(clientGlobalVendor.configs.image_category_setting);
      const fetchedOptions = imageCategoryKeys.map(key => ({
        label: key.charAt(0).toUpperCase() + key.slice(1).toLowerCase().replace(/_/g, ' '),
        value: key,
      }));

      setCategoryOptions(fetchedOptions);
    }
  }, [clientGlobalVendor]);

  // Set initial selected category based on imageCategory prop
  useEffect(() => {
    if (imageCategory) {
      const foundOption = categoryOptions.find(opt => opt.value === imageCategory);
      setSelectedCategory(
        foundOption || {
          label: imageCategory,
          value: imageCategory,
        },
      );
    }
  }, [imageCategory, categoryOptions]);

  const handleCategoryChange = option => {
    if (option) {
      setSelectedCategory(option);
      setIsNewCategory(option.__isNew__ || false);
      setShowModal(true);

      // If it's a new category, add it to the options
      if (option.__isNew__) {
        const newOption = { label: option.label, value: option.value };
        setCategoryOptions(prevOptions => [...prevOptions, newOption]);
      }
    } else {
      setSelectedCategory(null);
      setIsNewCategory(false);
    }
  };

  const handleModalClose = () => {
    setShowModal(false);
  };

  return (
    <>
      <Row>
        <Col sm="4">
          <Form.Group>
            <Form.Label>Image Category Setting</Form.Label>
            <CreatableSelect
              isClearable
              isSearchable
              isLoading={loading}
              options={categoryOptions}
              value={selectedCategory}
              onChange={handleCategoryChange}
              placeholder="Select or create an image category..."
              formatCreateLabel={inputValue => `Create new category "${inputValue}"`}
            />
          </Form.Group>
        </Col>
      </Row>
      <ImageCategoryModal
        show={showModal}
        onHide={handleModalClose}
        selectedCategory={selectedCategory}
        isNewCategory={isNewCategory}
        vendorId={vendorID}
      />
    </>
  );
}

ImageCategorySetting.propTypes = {
  imageCategory: PropTypes.string,
};

export default ImageCategorySetting;
